"use client";

import { useLayoutEffect, useRef } from "react";
import type { ChangeEvent } from "react";
import Input from "@/components/library/Input";
import {
  BusinessIdType,
  formatBusinessId,
  getBusinessIdCaretIndex,
} from "@/utils/verification/businessId";

interface BusinessIdInputProps {
  readonly type: BusinessIdType;
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly label: string;
  readonly placeholder?: string;
  readonly helperText?: string;
  readonly error?: boolean;
  readonly required?: boolean;
}

export function BusinessIdInput({
  type,
  value,
  onValueChange,
  label,
  placeholder,
  helperText,
  error,
  required,
}: BusinessIdInputProps) {
  const pendingCaretRef = useRef<{ input: HTMLInputElement; caret: number } | null>(null);
  const formattedValue = formatBusinessId(type, value);

  useLayoutEffect(() => {
    const pending = pendingCaretRef.current;
    if (!pending) {
      return;
    }
    pending.input.setSelectionRange(pending.caret, pending.caret);
    pendingCaretRef.current = null;
  }, [formattedValue]);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const rawValue = input.value;
    const formatted = formatBusinessId(type, rawValue);
    const caret = getBusinessIdCaretIndex(
      rawValue,
      input.selectionStart ?? rawValue.length,
      formatted
    );

    if (formatted === formattedValue) {
      input.setSelectionRange(caret, caret);
      return;
    }

    pendingCaretRef.current = { input, caret };
    onValueChange(formatted);
  };

  return (
    <Input
      type="text"
      label={label}
      placeholder={placeholder}
      required={required}
      value={formattedValue}
      helperText={helperText}
      error={error}
      onChange={onChange}
    />
  );
}
